import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';

import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

/**
 * Favorite listings of the current user. Favorites are saved as listing ids to the current user's
 * private data (profile.privateData.favorites). Unauthenticated users are redirected to the
 * signup page, and they return to the current page after signing up.
 *
 * @returns {Object} { isFavorite: listingId => boolean, toggleFavorite: listingId => void }
 */
export const useFavorites = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const routes = useRouteConfiguration();
  const currentUser = useSelector(state => state.user?.currentUser);

  const favorites = currentUser?.attributes?.profile?.privateData?.favorites || [];

  const isFavorite = listingId => !!listingId && favorites.includes(listingId);

  const toggleFavorite = useCallback(
    listingId => {
      if (!currentUser) {
        const state = { from: `${location.pathname}${location.search}${location.hash}` };
        history.push(createResourceLocatorString('SignupPage', routes, {}, {}), state);
        return;
      }
      const updatedFavorites = favorites.includes(listingId)
        ? favorites.filter(f => f !== listingId)
        : [...favorites, listingId];
      dispatch(updateProfile({ privateData: { favorites: updatedFavorites } }));
    },
    [currentUser, favorites, location, history, routes, dispatch]
  );

  return { isFavorite, toggleFavorite };
};

export default useFavorites;
